/**
 * Legal copy — Terms, Privacy, Responsible Gambling.
 *
 * Grounded in the real stack (Stripe, AWS ap-southeast-2, API-Sports; Privacy
 * Act 1988 APPs; the NDB scheme).
 *
 * The `LEGAL_REVIEWED` flag and the DRAFT banner it gated were removed on
 * 2026-08-03 at Ned's direction — he is a lawyer, and these are his documents,
 * so a banner saying they had not been reviewed by one was simply inaccurate.
 *
 * Placeholders in [[double brackets]] are facts still to be confirmed. They
 * render in a distinct style so they can't be missed. None render today.
 *
 * Lives in src/content/ on purpose: one folder for a lawyer to review, and the
 * banned-words gate covers it. The copy deliberately avoids the betting-
 * certainty words (it says "warrant"/"as is", "suspend", "chasing losses")
 * rather than carving a hole in that gate.
 */

/** Keep in one place; every document footer cites it. */
/*
 * The date the DOCUMENTS last changed, not the date this constant was edited.
 *
 * Was "17 July 2026" until 2026-08-26, which was wrong in a way a reader could
 * see: TERMS did not exist on that date — the repository's first commit is
 * 2026-07-19 — and the document was amended three times afterwards.
 *
 * Moved to 27 August 2026 because PRIVACY §1 and §2 actually changed on that
 * date: §1 now names the first and last name, date of birth and country of
 * residence that signup has always required, and the mailing-list record; §2
 * states what the name and date of birth are for, and stops claiming marketing
 * is a separate opt-in.
 *
 * ⚠️ This widens the gap with TERMS_VERSION below, which is STILL 2026-07-17 and
 * must stay there. The gap is not an inconsistency to tidy — closing it by
 * bumping TERMS_VERSION is the customer lockout described under that constant.
 * A privacy amendment does not trigger re-acceptance; only TERMS_VERSION does.
 */
export const LEGAL_LAST_UPDATED = "27 August 2026";

/**
 * The version recorded against a consent, sent to POST /me/consents and stored
 * beside the server timestamp.
 *
 * WHY IT EXISTS. "They accepted the terms" is worth very little without "which
 * terms" — and §180 of TERMS reserves the right to change them. This was missing
 * under the old checkbox too: a tick proved an act, never a document. Moving the
 * acceptance to a notice above the button made that gap worth closing rather than
 * causing it.
 *
 * Machine-sortable on purpose. `LEGAL_LAST_UPDATED` is human copy for the footer
 * and someone will eventually reformat it; this is the value an audit reads, so
 * it must not be the same string. **Bump it whenever TERMS changes materially.**
 *
 * ═══ 🛑 DELIBERATELY STALE, AND NOT SAFE TO "FIX" ═══
 *
 * This says 2026-07-17 while LEGAL_LAST_UPDATED directly above says 7 August
 * 2026, and the two disagree ON PURPOSE. Do not reconcile them by bumping this.
 *
 * It WAS bumped to "2026-08-07" on 2026-08-26, correctly, and reverted the same
 * day — because bumping it diverts every existing account to re-accept, and the
 * consent write is currently broken in a way that makes that a lockout rather
 * than a prompt. Checked against the deployed API with a real token:
 * POST /me/consents answers 200 with a fresh recordedAt, and GET /me goes on
 * reporting the OLD version and a consent row days older. First consent
 * persists; the update does not.
 *
 * So a bumped constant means: divert, accept, 200, /me unchanged, divert again,
 * forever. Every existing customer locked out of a product they have paid for,
 * by a call that told them it worked. Reverted is the only safe value until
 * /me reports the newest consent row, or the handler updates in place.
 *
 * LEGAL_LAST_UPDATED was NOT reverted with it. That constant is display copy on
 * the legal pages and triggers nothing; "7 August 2026" is simply the truth
 * about when the documents last changed, and putting a false date back on a
 * legal document to match an internal value would be the wrong repair.
 *
 * WHEN THE HANDLER IS FIXED: set this to "2026-08-07", which is the date the
 * rendered document last changed and is established below. guards.test.tsx has a
 * tripwire that fails the moment this moves, so nobody can do it without reading
 * this note.
 *
 * ═══ WHY 2026-08-07 IS THE RIGHT VALUE WHEN IT CAN BE USED ═══
 *
 * This said "2026-07-17" from the day it was introduced (2026-08-07) until it was
 * corrected on 2026-08-26, and that instruction directly above was never followed
 * — TERMS had already been amended three times by the time the constant existed.
 * §1 named a contracting party for the first time on 2026-07-31, and on
 * 2026-08-03 that party became a DIFFERENT COMPANY: ENCEE Analytics rather than
 * ENCEE Holdings, identified by ACN rather than ABN, with a postal address for
 * written notice added to §15. Who you are contracting with is as material as an
 * amendment gets.
 *
 * The cost was not only a wrong label. `guards.tsx` re-asks for acceptance when
 * a stored `termsVersion` differs from this constant, and its comment calls that
 * what makes this file's "reasonable notice before it takes effect" **"a
 * mechanism rather than an intention"**. Frozen, the mechanism never fired, so
 * every consent row recorded a version string describing text nobody had agreed
 * to.
 *
 * 2026-08-07 is the date the RENDERED document last changed, established by
 * hashing the TERMS declaration together with LEGAL_ENTITY — the prose
 * interpolates the entity, so changing that constant amends the document — with
 * comments stripped, across every revision of this file. Three later commits
 * touch it (2026-08-08 and twice on 2026-08-10) and none of them alter a word a
 * reader sees. Today's date was deliberately NOT used: this identifies the
 * document, and the document is not from today.
 *
 * Bumping it re-asks every existing account on its next visit. That was the point
 * of doing it before launch rather than after — the population it interrupts today
 * is test accounts.
 */
export const TERMS_VERSION = "2026-07-17";

/**
 * The contracting party.
 *
 * ENCEE ANALYTICS PTY LTD, ACN 700 988 790, is the legal entity (Ned,
 * 2026-08-03). "Run it Past Kev" is a TRADING NAME it holds, not a company — a
 * business name is an ASIC registration and can't be a party to a contract, so
 * every agreement here is with ENCEE, and the trading name appears only as a
 * "trading as".
 *
 * Get this the wrong way round and the customer's contract names an entity that
 * can't hold it, which puts the liability allocation in Terms §12 on nothing.
 *
 * ⚠️ CORRECTED 2026-08-03. This previously read "ENCEE Holdings Pty Ltd", which
 * was committed in fa91965 and was WRONG — the company is ENCEE Analytics. If
 * you find "Holdings" anywhere in this repo or in a draft sent to anyone, it is
 * a stale reference to an entity that is not the contracting party.
 *
 * ⚠️ Casing follows the conventional written form. If the ASIC record differs,
 * match the record — the registered name is what identifies the party.
 *
 * ACN, not ABN. Ned supplied the ACN (the 9-digit ASIC company number). An ABN
 * is a different 11-digit identifier and the company only has one if it
 * registered for it. It is NOT safe to derive one: the two-digit prefix is
 * computable from the ACN, but computing it would assert a tax registration
 * that may not exist, on a document that binds customers. Quoting the ACN is a
 * complete and correct way to identify an Australian company, so the documents
 * do that. Add the ABN when it is confirmed from the ABR — and it will be
 * needed regardless for valid tax invoices once the company is GST-registered.
 */
export const LEGAL_ENTITY = {
  name: "ENCEE Analytics Pty Ltd",
  tradingName: "Run it Past Kev",
  acn: "700 988 790",
  /** Confirmed by Ned, 2026-08-29. Note the check the comment above predicted:
      the last nine digits ARE the ACN (700 988 790), with the 81 prefix that
      makes it an ABN — which is exactly why deriving one was refused. Getting the
      prefix right by luck and wrong by arithmetic look identical on the page.

      Filling this in also switches GST display on everywhere — see
      `gstRegistered` below. That coupling is deliberate. */
  abn: "81 700 988 790",
  /* Ned, 2026-08-03. Rendered in Terms §15 and Privacy §9 — a value here that
     nothing reads is not a filled placeholder, it's an unwritten address. */
  address: "Suite 17, 89–97 Jones Street, Ultimo NSW 2007, Australia",
  /* Decided by Ned, 2026-07-31. See plans/00-master-schedule.md for the open
     question of whether to INCORPORATE elsewhere; that's a separate decision
     from which law governs these terms. */
  governingState: "New South Wales, Australia",
  contactGeneral: "hello@runitpastkev.com",
  contactPrivacy: "privacy@runitpastkev.com",
  /* Ned, 2026-08-30. DISTINCT FROM contactGeneral on purpose: support@ is the
     address handler_support.py ingests into the ticket queue, and it is the
     Reply-To on every message the Cognito pool sends. hello@ is a mailbox.
     Anything the product tells a customer to write to about their ACCOUNT
     belongs here, so the reply lands somewhere with a ticket behind it. */
  contactSupport: "support@runitpastkev.com",
} as const;

/**
 * May we state a GST component on a charge? Answered by the ABN, and only by it.
 *
 * ⚠️ THIS IS A TAX CLAIM, NOT A FORMATTING PREFERENCE. Representing that a price
 * includes GST is a statement that the company is registered and remitting it;
 * making it while unregistered is a misrepresentation, and the ATO requires an
 * ABN on any tax invoice that shows a GST amount at all.
 *
 * So it keys on the one field that records the answer. `LEGAL_ENTITY.abn` is
 * still the placeholder above, and its comment is explicit about why it was never
 * computed from the ACN: doing so "would assert a tax registration that may not
 * exist, on a document that binds customers". The same reasoning, applied to the
 * amount on a confirmation dialog.
 *
 * FILLING THE ABN IN IS THE WHOLE SWITCH. Confirm it from abr.business.gov.au,
 * paste it above, and every surface that can show GST starts doing so. Until
 * then they state the total and say nothing about tax — which is accurate, and
 * the safe direction besides.
 *
 * Note this is NOT the only source of a tax line: if Stripe Tax is configured, the
 * invoice carries a real `taxCents` and that is authoritative and preferred. This
 * covers the other case, where prices are advertised GST-INCLUSIVE and the tax is
 * a component of a total already agreed rather than something Stripe computed.
 */
export const gstRegistered = (): boolean =>
  LEGAL_ENTITY.abn.replace(/\D/g, "").length === 11;

/**
 * The GST inside a GST-INCLUSIVE amount: one eleventh of it.
 *
 * Not invented arithmetic — it is the ATO's standard formula for extracting the
 * tax component of a tax-inclusive price (10% added means the tax is 1/11 of the
 * total, not 1/10). The pricing page advertises "Prices in AUD, GST included", so
 * the total is the agreed figure and this names the part of it that is tax.
 *
 * Rounded to the cent, so the parts can differ from the total by a cent — which is
 * why the TOTAL is always the figure taken from Stripe and never a sum of the
 * lines rendered beside it.
 */
export const gstIncludedInCents = (totalCents: number): number =>
  Math.round(totalCents / 11);

/**
 * The support channel, said out loud (2026-08-10).
 *
 * The address is not new — it is in Terms §15, in Privacy §9, on the geo-refusal
 * page and in the noscript block of index.html. What was missing was anywhere to
 * click from INSIDE the product. A signed-in customer with a payment they did not
 * expect had the Stripe portal, which cannot answer a question, and nothing else.
 *
 * Deliberately a mailto rather than a page or a form. A support page implies a
 * knowledge base to fill and a form implies a queue to work, and neither exists;
 * the honest surface is the mailbox that is genuinely read. It also keeps this
 * out of the public route manifest, so no crawlable page has to be maintained to
 * say one address.
 *
 * `label` is deliberately not "Help" — the footer's other support-shaped text is
 * Gambling Help Online, and two things called Help sitting a line apart is the
 * one confusion this surface cannot afford.
 */
export const SUPPORT = {
  label: "Support",
  /** Footer link title, and the accessible name for a bare-address link. */
  linkTitle: `Email ${LEGAL_ENTITY.contactGeneral}`,
  /** The account-page line. Says what it is FOR, since that is the page where a
      billing question actually occurs to someone. */
  accountLine:
    "Something not right with your plan, your credits, or your account? Email us and a human will read it.",
} as const;

export interface LegalSection {
  h: string;
  /** Each string is a paragraph. A nested list is one paragraph with " • " items. */
  body: string[];
}

export interface LegalDoc {
  slug: string;
  title: string;
  eyebrow: string;
  intro: string[];
  sections: LegalSection[];
}

/* ─────────────────────────── Terms of Service ─────────────────────────── */

export const TERMS: LegalDoc = {
  slug: "terms",
  title: "Terms of Service",
  eyebrow: "The Rules",
  intro: [
    /* Interpolated, never retyped. The entity name was hardcoded in this string
       and in the Privacy intro, so when the company turned out to be ENCEE
       Analytics rather than ENCEE Holdings, both documents kept naming a
       company that is not the contracting party. One constant, two readers. */
    `These terms are the agreement between you and ${LEGAL_ENTITY.name} (ACN ${LEGAL_ENTITY.acn}) trading as “${LEGAL_ENTITY.tradingName}” — “KEV”, “we”, “us” — for your use of runitpastkev.com and the KEV apps (the “Service”).`,
    "By creating an account or using the Service you accept these terms. If you don’t accept them, don’t use the Service.",
  ],
  sections: [
    {
      h: "1. You must be 18 or over",
      body: [
        "The Service is for adults only. You confirm you are at least 18 years of age. This applies to every part of the Service, including the fantasy models — the framing doesn’t change the rule.",
        "We may ask you to confirm your age and may suspend or close an account we reasonably believe belongs to a minor.",
      ],
    },
    {
      h: "2. What KEV is — and what it isn’t",
      body: [
        "KEV is an analytics product. Its models produce projections, comparisons and commentary about sport. That is information, presented so you can draw your own conclusions.",
        "KEV is not a tipping service, a bookmaker, or a financial adviser. It does not tell you how much to stake, and nothing in the Service is a recommendation to place any bet or an assurance of any outcome. Sport is uncertain and models are wrong plenty of the time.",
        "Any decision you make with money is yours alone. You are responsible for complying with the laws that apply to you, including any laws about wagering in your state or territory.",
      ],
    },
    {
      h: "3. Accuracy, completeness and timeliness",
      body: [
        "We work to keep the information in the Service complete, current and accurate. We can't warrant that it is. Sports data arrives late, gets corrected after the fact, and sometimes disagrees between sources, and a model built on it inherits every one of those problems.",
        "Projections are estimates. They describe what our models expect across many similar situations, not what will happen in the one you're looking at. How a model, a team or a player has performed before is no assurance of what comes next.",
        "You are responsible for checking anything you intend to act on. To the extent the law allows, we accept no liability for a decision you make on the basis of information in the Service, including where that information turns out to be incomplete, out of date or wrong.",
      ],
    },
    {
      h: "4. Your account",
      body: [
        "You need an account to use most of the Service. Keep your login details private — you are responsible for activity under your account. Tell us promptly at " +
          "hello@runitpastkev.com if you believe someone else has accessed it.",
        "You must give accurate details and a valid email address, and verify that address. One person, one account, unless we agree otherwise in writing.",
        "You may not share, sell or transfer your account or your access, and you may not use another person’s account.",
      ],
    },
    {
      h: "5. Subscriptions, credits and payment",
      body: [
        "Some parts of the Service are paid — as a per-model subscription or an all-model plan. Prices are shown at checkout in Australian dollars and include GST where it applies.",
        "Payments are processed by Stripe. We never see or store your full card details — Stripe holds them. Your use of the payment flow is also subject to Stripe’s terms.",
        "Paid plans renew automatically each cycle until you cancel. You can cancel any time; cancellation stops the next renewal and you keep access until the end of the current cycle. Except where the law requires otherwise, payments already made are not refunded for time you didn’t use.",
        "Each plan includes a monthly allowance of credits. Credits are a unit we use to meter usage of the models. They have no cash value, cannot be exchanged for money, expire at the end of each cycle unless stated otherwise, and are not transferable. We may change credit costs and allowances on reasonable notice.",
      ],
    },
    {
      h: "6. Beta models, promotions and trials",
      body: [
        "Some models are offered as Beta. A Beta model works and answers questions, but it's still being tuned, and its outputs and features may change or be withdrawn while it stays in Beta.",
        "We may offer promotional pricing, including a reduced Beta rate. If you subscribe to a model at its Beta rate, you keep that rate for as long as that subscription stays active, even after the model leaves Beta. Members who join after that pay the standard price.",
        "If you cancel, the Beta rate goes with the subscription — resubscribing later means paying whatever the price is then. If we ever need to change a price you're already paying, we'll give you reasonable notice first and you can cancel before it takes effect.",
        "Promotions and any competitions we run may have their own terms, which apply alongside these. Unless we say otherwise, one free trial or promotional rate is available per person and per household.",
      ],
    },
    {
      h: "7. Fair use, and protecting the Service",
      body: [
        "The models cost real money to run. You agree to use the Service as intended and not to: automate or script access; resell or redistribute the outputs; try to extract the models, prompts or training data; probe, overload or interfere with the Service; or use it to build a competing product.",
        "We apply rate limits, usage caps and other controls to keep the Service available and affordable for everyone. We may suspend or restrict access that we reasonably believe is abusive, automated, or a threat to the Service or other users — including where an account exceeds fair-use limits.",
      ],
    },
    {
      h: "8. Acceptable use",
      body: [
        "Don’t use the Service to break the law, to harass anyone, to infringe anyone’s rights, or to get around its limits or security. Don’t upload anything unlawful, and don’t try to make the models produce content outside what the Service is for.",
        "Don’t upload or transmit anything that carries a virus or other harmful code, and don’t post material that is defamatory, misleading, or that invades someone’s privacy.",
      ],
    },
    {
      h: "9. Intellectual property, and personal use only",
      body: [
        "The Service, the KEV and Franco personas, the models, the branding and the site are owned by us or our licensors. We grant you a personal, non-exclusive, non-transferable licence to use the Service for your own, non-commercial use while these terms apply. You get no other rights.",
        "You may not reproduce, republish, redistribute, sell, or otherwise commercially exploit any part of the Service or its outputs, or make them available to anyone else, without our written permission. This includes republishing projections as your own and reselling access.",
        "Underlying sports data is provided by third-party licensors and remains subject to their rights.",
      ],
    },
    {
      h: "10. Links to other sites",
      body: [
        "The Service may link to sites we don’t operate. We don’t control them, we don’t endorse what they contain, and we’re not responsible for their content, their products, or how they handle your information. Their terms and privacy policies apply when you’re there, not ours.",
      ],
    },
    {
      h: "11. Availability, and “as is”",
      body: [
        "We work to keep the Service available but we don’t warrant that it will be uninterrupted, error-free, or that any projection will be accurate. The Service, and everything in it, is provided “as is” and “as available”.",
        "To the extent the law allows, we exclude all warranties that aren’t expressly stated here.",
      ],
    },
    {
      h: "12. Australian Consumer Law, and our liability",
      body: [
        "Nothing in these terms excludes, restricts or modifies any right or remedy you have under the Australian Consumer Law or other law that cannot lawfully be excluded. Where our liability can be limited, it is limited — at our option — to re-supplying the Service or paying the cost of having it re-supplied.",
        "Otherwise, and to the extent the law allows, we are not liable for any direct, indirect or consequential loss arising from your use of the Service, from information in it being inaccurate, incomplete or out of date, or from your betting or wagering decisions.",
      ],
    },
    {
      h: "13. Suspension and closing your account",
      body: [
        "You can close your account any time from your account settings or by emailing us. We may suspend or close an account for a breach of these terms, for conduct that risks the Service or other users, or where we’re required to by law — with notice where it’s reasonable to give it.",
        "When an account closes, your right to use the Service ends. We handle your data as set out in the Privacy Policy, including our deletion process.",
      ],
    },
    {
      h: "14. Changes to these terms",
      body: [
        "We may update these terms. If a change materially affects you, we’ll give reasonable notice — by email or in the Service — before it takes effect. Continuing to use the Service after that means you accept the updated terms.",
      ],
    },
    {
      h: "15. Governing law and contact",
      body: [
        "These terms are governed by the laws of New South Wales, Australia. You and we submit to the non-exclusive jurisdiction of the courts of New South Wales and the courts that hear appeals from them.",
        `Questions about these terms: ${LEGAL_ENTITY.contactGeneral}. Written notice can be sent to ${LEGAL_ENTITY.name} (ACN ${LEGAL_ENTITY.acn}), ${LEGAL_ENTITY.address}.`,
      ],
    },
  ],
};

/* ────────────────────────────── Privacy ──────────────────────────────── */

export const PRIVACY: LegalDoc = {
  slug: "privacy",
  title: "Privacy Policy",
  eyebrow: "Your Data",
  intro: [
    `This policy explains how ${LEGAL_ENTITY.name} (ACN ${LEGAL_ENTITY.acn}) trading as “${LEGAL_ENTITY.tradingName}” handles your personal information. We follow the Australian Privacy Principles (APPs) under the Privacy Act 1988 (Cth).`,
    "It covers what we collect, why, who we share it with, where it’s stored, and the rights you have over it.",
  ],
  sections: [
    {
      h: "1. What we collect",
      body: [
        "Account information: your email address, your first and last name, your date of birth, and your country of residence. All of these are required to create an account. Your name identifies you as the account holder, and we may use it together with your date of birth to verify your identity and confirm you’re old enough to use the Service, including where the law requires us to. Your country of residence tells us whether we’re able to serve you. Sign-in is managed by our identity provider, and we never store your password in readable form.",
        "Subscription and payment information: which plan you’re on and your billing status. Card details are collected and held by Stripe, not by us — we receive only a token and the status of a payment.",
        "Usage information: the questions you ask the models, the responses, your credit balance and activity, and technical logs (device, approximate location from IP, timestamps) needed to run and secure the Service.",
        "Mailing list information: if you give us your email address to hear about the launch, we record that address, when you gave it, the page you gave it on, and the IP address the request came from. We keep those as our record of when and how you subscribed, and we collect them whether or not you go on to create an account.",
        "We do not seek sensitive information, and we ask you not to include it in your messages to the models.",
      ],
    },
    {
      h: "2. Why we use it",
      body: [
        "To provide the Service — run the models, meter credits, and keep your history. • To identify you as the account holder, and to verify your identity and that you’re old enough to use the Service where we need to — which is what your name and date of birth are for. • To confirm you’re in a country we’re able to serve. • To take payment and manage your subscription. • To keep the Service secure and prevent abuse, including enforcing usage limits and detecting fraud. • To support you when you get in touch. • To meet our legal obligations, including responsible-gambling and age requirements. • To improve the Service. We rely on your messages only in aggregate or de-identified form for improvement, unless you’ve agreed otherwise.",
        "Marketing email is part of what you agree to when you create an account or join our mailing list. It isn’t a separate tick — we tell you plainly, at the point you sign up, that joining means marketing email. Every marketing message carries a one-click unsubscribe, we act on it, and you can unsubscribe at any time. Service and account emails (billing, security, verification) are not marketing and are sent as needed.",
      ],
    },
    {
      /**
       * ⚠️ RECIPIENTS AND WHAT EACH ONE GETS. NOTHING ELSE. Ned, 2026-08-04.
       *
       * Two rules for this section, in his words: say who the data is shared with
       * and what may be shared; stay silent where data is NOT shared.
       *
       * So each entry pairs a named recipient with the categories that actually
       * reach it, and parties we send nothing to are simply absent. An earlier
       * draft listed API-Sports and balldontlie with a "we send them nothing about
       * you" clause — do not put that back. Naming a non-recipient in the sharing
       * section invites the reader to believe a flow exists that the sentence then
       * denies, and it pads a list whose whole value is that it is short and true.
       * Those feeds belong in §1/§2 if anywhere; they are inbound.
       *
       * AWS has to be named, and named as more than a host: the MODELS run on
       * Amazon Bedrock inside our own account, so the questions people type are
       * processed there. "AI model providers" — the phrasing still in the ToS
       * draft in RIPK/kev-docs — implies a separate AI company receives them.
       * Cognito (sign-in) and SES (email) are AWS too, so they are one entry, not
       * three unnamed vendors implying a longer supply chain than we have.
       */
      h: "3. Who we share it with",
      body: [
        "We don’t sell your personal information. We share it with the providers below, under agreements that limit what they can do with it, and each receives only what it needs:",
        "Amazon Web Services — your account details, the questions you ask and the answers you get, your credit and usage records, and technical logs. AWS hosts the Service, stores its data, manages sign-in, sends your account email, and runs the models themselves on Amazon Bedrock inside our own AWS account. • Stripe — your email address, your plan, and your payment status, so we can take payment and manage your subscription.",
        "We may also disclose information where the law requires it, or to protect our rights, users or the Service.",
      ],
    },
    {
      h: "4. Where your data is stored, and cross-border disclosure",
      body: [
        "We host the Service in AWS’s Sydney region (ap-southeast-2) to keep your primary data in Australia.",
        "Some providers (for example Stripe, and some support tooling) may process limited information outside Australia. Where that happens we take reasonable steps to ensure it’s handled consistently with the APPs. This is our APP 8 cross-border disclosure notice.",
      ],
    },
    {
      /**
       * ⚠️ The 24 hours below is a FACT ABOUT THE SYSTEM, not a target.
       *
       * Source of truth is `RETENTION_HOURS` in api/kev_history.py, which every
       * tier currently shares (`_RETENTION_BY_TIER` maps free/premium/pro to the
       * same value). The DynamoDB TTL is what actually deletes the rows.
       *
       * Do not write longer per-tier windows here until that constant changes
       * and ships. A "history kept for a year" perk line was deleted from
       * plans.ts on 2026-07-31 for exactly this reason, and the note there calls
       * it what it is: ACL misleading conduct, not a style nit. A retention
       * promise in the PRIVACY POLICY is the more binding version of that
       * mistake — it tells someone their data still exists when it's gone.
       *
       * When longer windows are built: change the server constant, deploy, then
       * update this paragraph and the ConversationList copy from it.
       */
      h: "5. How long we keep it",
      body: [
        "We keep account and billing records for as long as your account is open and then as long as the law requires (for example, tax and financial records).",
        "Conversation history is kept for 24 hours from the last time you were active in a conversation, and is then deleted automatically. This is the same on every plan, paid or free. If you come back to a conversation the 24 hours starts again from that point; leave it alone and it goes.",
        "Treat the models as somewhere you ask a question, not somewhere you store anything. If you want to keep an answer, copy it out.",
        "Security and audit logs are kept for a limited period and then deleted.",
      ],
    },
    {
      h: "6. Accessing, correcting and deleting your data",
      body: [
        "You can view and update your account details in the Service. You can ask us for a copy of the personal information we hold about you, and ask us to correct it if it’s wrong.",
        "You can delete your account. When you do, we begin a deletion process that removes your personal information from our live systems and expires it from backups, and we aim to complete it within 30 days. Some records may be retained where the law requires, and only for as long as it requires.",
        "To make any of these requests, email privacy@runitpastkev.com. We’ll verify it’s you before acting.",
      ],
    },
    {
      h: "7. Security",
      body: [
        "We protect your information with measures appropriate to its sensitivity: encryption in transit and at rest, access controls, verified sign-in, and monitoring. No system is perfectly secure, but we design to keep each account isolated from every other.",
        "If a data breach occurs that is likely to cause you serious harm, we’ll respond under the Notifiable Data Breaches scheme — assessing promptly and, where required, notifying you and the Office of the Australian Information Commissioner.",
      ],
    },
    {
      h: "8. Cookies and similar technology",
      body: [
        "We use a small number of cookies and similar technologies that are necessary to sign you in and keep the Service working. Where we use anything beyond what’s necessary, we’ll ask for your consent first and you can decline.",
      ],
    },
    {
      h: "9. Complaints and contact",
      body: [
        /* APP 1.4(b) wants the policy to say how an individual can contact us
           and complain, and a postal address is the form that doesn't assume
           the reader can or wants to use email. */
        `Our privacy contact is ${LEGAL_ENTITY.contactPrivacy}. You can also write to us at ${LEGAL_ENTITY.name} (ACN ${LEGAL_ENTITY.acn}), ${LEGAL_ENTITY.address}.`,
        "If you’re not satisfied with how we’ve handled a privacy matter, you can complain to the Office of the Australian Information Commissioner at oaic.gov.au.",
      ],
    },
  ],
};

/* ──────────────────────── Responsible Gambling ───────────────────────── */

export const RESPONSIBLE_GAMBLING: LegalDoc = {
  slug: "responsible-gambling",
  title: "Responsible Gambling",
  eyebrow: "Look After Yourself",
  intro: [
    "No model can predict sport with certainty, including ours.",
    "If betting is part of how you use what KEV tells you, keep it in proportion.",
  ],
  sections: [
    {
      h: "The one rule",
      body: [
        "Only ever bet what you can afford to lose, and treat it as entertainment — not as a way to make money or to recover money you’ve already lost.",
        "Imagine what you could be buying instead.",
        "KEV will never tell you how much to stake or what to back. A number that looks good is still a bet that loses plenty of the time.",
      ],
    },
    {
      h: "What our numbers can and can’t tell you",
      body: [
        "We work to keep KEV’s data complete, current and accurate, and we can’t warrant that it is. Sports data arrives late and gets corrected, and a projection is an estimate across many similar situations rather than a statement about the one game in front of you.",
        "Treat a KEV number as one input, not as a reason to stake more than you planned. We take no liability for decisions made on it. Terms §3 sets this out in full.",
      ],
    },
    {
      h: "Signs it might be becoming a problem",
      body: [
        "Betting more than you planned, or more than you can afford. • Chasing losses — betting to win back money you’ve lost. • Betting to escape stress or low mood. • Hiding it, or lying about it. • Borrowing money, or falling behind on bills, to bet. • It stops being fun.",
        "If any of these sound familiar, it’s worth pausing. Talking to someone helps.",
      ],
    },
    {
      h: "Where to get help — free, confidential, 24/7",
      body: [
        "Gambling Help Online — 1800 858 858, or gamblinghelponline.org.au. Free counselling and support, any time, across Australia.",
        "Lifeline — 13 11 14, for crisis support and anyone in distress.",
        "If you or someone else is in immediate danger, call 000.",
      ],
    },
    {
      h: "Tools to stay in control",
      body: [
        "Set your own limits before you start, and stick to them. • Take regular breaks. • Never bet on credit. • Consider the self-exclusion and deposit-limit tools that betting operators are required to offer, and the national self-exclusion register, BetStop (betstop.gov.au).",
        "KEV is analytics, not a betting operator, so we don’t hold deposits or place bets.",
      ],
    },
  ],
};

/**
 * Shown wherever KEV puts a PRICE or a probability in front of someone.
 *
 * WHY IT IS HERE AND NOT IN THE PERSONA. Kev is a language model. A disclaimer
 * he is instructed to add is a disclaimer he adds MOST of the time, and the
 * turns he forgets are indistinguishable from the ones he doesn't — which is
 * the opposite of what a compliance line is for. Rendered by the app, it is on
 * screen for every priced answer whatever the model does, including when the
 * model is having a bad day or someone edits the prompt.
 *
 * WHY IT IS A CONDENSED COPY OF THE TERMS, not new prose: every clause below
 * restates Terms §2 ("not a tipping service… does not tell you how much to
 * stake… models are wrong plenty of the time") and §3 ("we accept no liability
 * for a decision you make on the basis of information in the Service").
 * Freshly-written wording on a surface people actually read, sitting alongside
 * Terms nobody opens, is how the two end up saying different things — and the
 * short version is the one a court would treat as what the customer was told.
 *
 * Keep it to three lines. A wall of text next to every price gets scrolled past,
 * and a disclaimer nobody reads is decoration.
 */
/**
 * Which wording of the disclaimer this bundle is showing (2026-08-10).
 *
 * Same mechanism as TERMS_VERSION, and for the same reason: the server records
 * the version it was handed, the client compares it against this constant, and
 * absent-or-older puts the overlay back in front of the chat. That is what makes
 * a rewrite reach the people who have already clicked once — without it, the
 * first acknowledgement would silently cover every future version of the text.
 *
 * ⚠️ BUMP THIS WHEN PRICING_DISCLAIMER CHANGES BELOW. A reworded disclaimer on
 * an unchanged version is a disclaimer nobody is ever shown again.
 */
export const DISCLAIMER_VERSION = "2026-08-10";

export const PRICING_DISCLAIMER = [
  "Kev prices what the models project. He doesn't tell you what to back, or how much — no staking advice, ever.",
  "Models are wrong plenty of the time. A price is an estimate across many similar situations, not a call on this one.",
  "What you do with your money is yours. We take no responsibility for the outcome of any bet.",
] as const;

/**
 * The nearing-a-limit flag, shown in the chat.
 *
 * WHY IT EXISTS. The running credit meter was deleted from the chat header, the
 * session panel and the directory on 2026-07-31, and each of those three
 * comments records the same consequence: nothing in the signed-in product
 * counted down any more, so running out arrived as the §6.3 panel with no
 * warning at all. That trade was deliberate — a permanent countdown turns every
 * question into a purchase decision — but "no meter" and "no warning" are not
 * the same thing, and only the first was actually wanted (Ned, 2026-08-10).
 *
 * So: not a number, and not always on. A line that appears only when a limit is
 * genuinely close, and sends the reader somewhere that can answer properly.
 *
 * TWO LIMITS, NAMED SEPARATELY. Credits and the daily game cap bind at different
 * times and have different remedies — one waits a month or is topped up, the
 * other waits until tomorrow. Commit 43c4120 ("Say which limit actually binds:
 * credits, not the daily game cap") exists because conflating them has already
 * confused someone once.
 */
export const LIMIT_FLAG = {
  credits: "You\u2019re nearing your monthly credit allowance.",
  daily: "You\u2019re nearing today\u2019s game limit \u2014 it resets tomorrow.",
  /** Shared tail. The account page is where the numbers live, so this points at
      it rather than restating them here and having two places to keep true. */
  where: "See your account for the detail, or top up.",
  linkLabel: "Your account",
} as const;

export const LEGAL_DOCS = [TERMS, PRIVACY, RESPONSIBLE_GAMBLING] as const;
